import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileText, Download, Upload, Sparkles, AlertCircle, CheckCircle2, Clock, Calendar, Search } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/excelExport";

import { compressSchemePdf } from "@/lib/pdfCompressor";

export default function Schemes() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAccountsOrAdmin = ["admin", "accounts", "financial_head"].includes(user?.role);

  const [activeTab, setActiveTab] = useState("circulars");
  const [search, setSearch] = useState("");
  const [previewPdf, setPreviewPdf] = useState(null); // url to preview in iframe modal
  const [uploadOpen, setUploadOpen] = useState(false);

  // Upload state
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newCategory, setNewCategory] = useState("Smartphones");
  const [newValidFrom, setNewValidFrom] = useState(new Date().toISOString().slice(0, 10));
  const [newValidUntil, setNewValidUntil] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [compressedInfo, setCompressedInfo] = useState(null); // { originalSize, newSize, savedPct }
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressMsg, setCompressMsg] = useState("");

  // AI Reconciler state
  const [reconcileSchemeFile, setReconcileSchemeFile] = useState(null);
  const [reconcilePayoutFile, setReconcilePayoutFile] = useState(null);
  const [auditResult, setAuditResult] = useState(null);
  const [isReconciling, setIsReconciling] = useState(false);

  // Handle PDF file selection with instant lossless client-side compression
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      setCompressedInfo(null);
      return;
    }

    if (file.type !== "application/pdf") {
      toast.error("Please upload a PDF document.");
      return;
    }

    setIsCompressing(true);
    setCompressMsg("Optimizing PDF formatting & streams...");

    try {
      const { optimizedFile, originalSize, newSize, savedPct } = await compressSchemePdf(
        file,
        (progress, msg) => setCompressMsg(msg)
      );

      setSelectedFile(optimizedFile);
      setCompressedInfo({
        originalSize: (originalSize / 1024 / 1024).toFixed(2),
        newSize: (newSize / 1024).toFixed(1),
        savedPct,
      });

      if (savedPct > 0) {
        toast.success(`PDF optimized: ${savedPct}% smaller with zero formatting loss!`);
      }
    } catch (err) {
      console.error("PDF compression error:", err);
      setSelectedFile(file);
    } finally {
      setIsCompressing(false);
      setCompressMsg("");
    }
  };

  // Load schemes from backend
  const { data: schemes = [], isLoading } = useQuery({
    queryKey: ["schemes-list"],
    queryFn: async () => {
      const { data } = await api.get("/schemes");
      return data;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (formData) => {
      const { data } = await api.post("/schemes", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return data;
    },
    onSuccess: () => {
      toast.success("Scheme circular published successfully!");
      setUploadOpen(false);
      setNewTitle("");
      setNewDesc("");
      setSelectedFile(null);
      setCompressedInfo(null);
      queryClient.invalidateQueries(["schemes-list"]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to upload scheme circular");
    }
  });

  const handleUploadSubmit = (e) => {
    e.preventDefault();
    if (!newTitle || !newValidFrom || !newValidUntil || !selectedFile) {
      return toast.error("Please fill in title, validity dates, and select a PDF file.");
    }
    const formData = new FormData();
    formData.append("title", newTitle);
    formData.append("description", newDesc);
    formData.append("category", newCategory);
    formData.append("valid_from", newValidFrom);
    formData.append("valid_until", newValidUntil);
    formData.append("file", selectedFile);
    uploadMutation.mutate(formData);
  };

  const handleRunAiAudit = async () => {
    if (!reconcileSchemeFile || !reconcilePayoutFile) {
      return toast.error("Please select both a Scheme Policy PDF and a Samsung Payout Excel file.");
    }

    setIsReconciling(true);
    setAuditResult(null);

    const formData = new FormData();
    formData.append("scheme_pdf", reconcileSchemeFile);
    formData.append("payout_excel", reconcilePayoutFile);

    try {
      const { data } = await api.post("/schemes/reconcile", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setAuditResult(data);
      toast.success("AI Scheme Audit completed successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reconciliation failed. Check file formats.");
    } finally {
      setIsReconciling(false);
    }
  };

  const filteredSchemes = schemes.filter(s => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (s.title || "").toLowerCase().includes(term) || (s.category || "").toLowerCase().includes(term);
  });

  const todayStr = new Date().toISOString().slice(0, 10);

  const getSchemeStatus = (s) => {
    if (s.valid_until < todayStr) return { label: "Expired", color: "bg-slate-100 text-slate-600 border-slate-200" };
    const diffDays = (new Date(s.valid_until) - new Date(todayStr)) / (1000 * 60 * 60 * 24);
    if (diffDays <= 3) return { label: "Ending Soon", color: "bg-amber-50 text-amber-800 border-amber-200 font-semibold" };
    return { label: "Active", color: "bg-emerald-50 text-emerald-700 border-emerald-200 font-medium" };
  };

  return (
    <div className="space-y-6" data-testid="schemes-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Samsung Trade Portal</div>
          <h1 className="mt-1 text-3xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>
            Schemes & Trade Circulars
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Access official Samsung trade schemes, sell-out targets, payout policies, and AI audit tools.
          </p>
        </div>
        {isAccountsOrAdmin && (
          <div className="flex items-center gap-3">
            <Button onClick={() => setUploadOpen(true)} className="bg-[#0055FF] hover:bg-[#0044CC] h-9">
              <Upload size={14} className="mr-1.5" /> Upload Scheme PDF
            </Button>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 p-1 border border-slate-200">
          <TabsTrigger value="circulars" className="text-xs">
            <FileText size={14} className="mr-1.5" /> All Scheme Circulars ({schemes.length})
          </TabsTrigger>
          {isAccountsOrAdmin && (
            <TabsTrigger value="ai-audit" className="text-xs flex items-center gap-1 text-purple-700 font-medium">
              <Sparkles size={14} className="text-purple-600" /> AI Payout Reconciler
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab 1: Circulars Grid */}
        <TabsContent value="circulars" className="space-y-4">
          <Card className="p-3 bg-white border border-slate-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
              <Input
                placeholder="Search schemes by title or product line..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
          </Card>

          {isLoading ? (
            <div className="py-16 text-center text-sm text-slate-500">Loading scheme circulars...</div>
          ) : filteredSchemes.length === 0 ? (
            <Card className="p-12 text-center text-slate-500 border border-slate-200">
              <FileText size={36} className="mx-auto text-slate-300 mb-2" />
              <div className="font-medium text-slate-700">No scheme circulars found</div>
              <div className="text-xs text-slate-400 mt-1">Schemes uploaded by the finance team will appear here.</div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredSchemes.map(s => {
                const status = getSchemeStatus(s);
                return (
                  <Card key={s.id} className="p-5 flex flex-col justify-between border border-slate-200 hover:shadow-md transition-shadow">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <Badge variant="outline" className={`text-[10px] ${status.color}`}>
                          {status.label}
                        </Badge>
                        <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                          {s.category || "General"}
                        </span>
                      </div>
                      <h3 className="font-semibold text-sm text-slate-900 leading-snug line-clamp-2" title={s.title}>
                        {s.title}
                      </h3>
                      {s.description && (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{s.description}</p>
                      )}
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <Calendar size={13} className="text-slate-400" />
                        <span>{s.valid_from} to {s.valid_until}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => setPreviewPdf(s.pdf_url)}
                          className="h-8 text-xs px-2.5"
                        >
                          View
                        </Button>
                        <a 
                          href={s.pdf_url} 
                          download={s.file_name || "Scheme.pdf"}
                          target="_blank" 
                          rel="noreferrer"
                          className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                        >
                          <Download size={14} />
                        </a>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Tab 2: AI Reconciler (Accounts/Admin) */}
        {isAccountsOrAdmin && (
          <TabsContent value="ai-audit" className="space-y-6">
            <Card className="p-6 border border-purple-200 bg-gradient-to-br from-purple-50/50 via-white to-slate-50">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center shrink-0">
                  <Sparkles size={20} />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-slate-900">
                    Gemini AI Scheme & Payout Auditor
                  </h2>
                  <p className="text-xs text-slate-600 mt-1">
                    Upload the official Samsung Scheme Policy PDF alongside Samsung's Payout working file (.xlsx/.xlsb). 
                    Gemini automatically reconciles qualification slabs, verifies dealer payouts, and extracts claim shortfalls.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                    {/* Scheme PDF Drop */}
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-white text-center hover:border-purple-400 transition-colors">
                      <FileText size={24} className="mx-auto text-purple-600 mb-1" />
                      <div className="text-xs font-semibold text-slate-800">1. Select Scheme Letter (PDF)</div>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setReconcileSchemeFile(e.target.files[0])}
                        className="text-xs text-slate-500 mt-2 block w-full file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                      />
                      {reconcileSchemeFile && (
                        <div className="text-[11px] text-emerald-600 mt-1 font-medium">Selected: {reconcileSchemeFile.name}</div>
                      )}
                    </div>

                    {/* Payout Excel Drop */}
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-white text-center hover:border-purple-400 transition-colors">
                      <FileText size={24} className="mx-auto text-emerald-600 mb-1" />
                      <div className="text-xs font-semibold text-slate-800">2. Select Payout Working (.xlsx / .xlsb)</div>
                      <input
                        type="file"
                        accept=".xlsx,.xlsb,.csv"
                        onChange={(e) => setReconcilePayoutFile(e.target.files[0])}
                        className="text-xs text-slate-500 mt-2 block w-full file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                      />
                      {reconcilePayoutFile && (
                        <div className="text-[11px] text-emerald-600 mt-1 font-medium">Selected: {reconcilePayoutFile.name}</div>
                      )}
                    </div>
                  </div>

                  <div className="mt-5 flex justify-end">
                    <Button
                      onClick={handleRunAiAudit}
                      disabled={isReconciling || !reconcileSchemeFile || !reconcilePayoutFile}
                      className="bg-purple-600 hover:bg-purple-700 text-white h-9"
                    >
                      <Sparkles size={14} className="mr-1.5" />
                      {isReconciling ? "Gemini Auditing Scheme Rules & Payouts..." : "Run AI Scheme Audit"}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>

            {/* Audit Results Dashboard */}
            {auditResult && (
              <Card className="p-6 border border-slate-200 bg-white space-y-6">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Audit Complete</div>
                    <h3 className="text-lg font-bold text-slate-900 mt-0.5">{auditResult.scheme_title || "Scheme Audit Report"}</h3>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadCsv("Samsung_Payout_Dispute_Sheet.csv", ["dealer_code", "dealer_name", "expected_inr", "actual_inr", "variance_inr", "reason"], auditResult.discrepancies || [])}
                    className="h-8 text-xs border-purple-300 text-purple-800"
                  >
                    <Download size={13} className="mr-1" /> Export Dispute Excel
                  </Button>
                </div>

                {/* KPI Metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                    <div className="text-xs text-slate-500">Total Claimable Payout</div>
                    <div className="text-xl font-bold text-slate-900 mt-1">₹{Number(auditResult.total_claimable_inr || 0).toLocaleString('en-IN')}</div>
                  </div>
                  <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                    <div className="text-xs text-emerald-700">Samsung Disbursed Amount</div>
                    <div className="text-xl font-bold text-emerald-800 mt-1">₹{Number(auditResult.total_disbursed_inr || 0).toLocaleString('en-IN')}</div>
                  </div>
                  <div className="p-4 rounded-lg bg-rose-50 border border-rose-200">
                    <div className="text-xs text-rose-700">Shortfall / Dispute Amount</div>
                    <div className="text-xl font-bold text-rose-800 mt-1">₹{Number(auditResult.shortfall_inr || 0).toLocaleString('en-IN')}</div>
                  </div>
                </div>

                {/* Discrepancy Table */}
                <div>
                  <h4 className="font-semibold text-sm text-slate-800 mb-2">Disputed Dealer Payouts ({auditResult.discrepancies?.length || 0})</h4>
                  <div className="overflow-x-auto border border-slate-200 rounded-md">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 text-slate-600 uppercase text-[10px]">
                        <tr>
                          <th className="p-2.5 text-left">Dealer Code</th>
                          <th className="p-2.5 text-left">Dealer Name</th>
                          <th className="p-2.5 text-right">Expected (₹)</th>
                          <th className="p-2.5 text-right">Paid (₹)</th>
                          <th className="p-2.5 text-right">Variance (₹)</th>
                          <th className="p-2.5 text-left">Audit Finding / Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(auditResult.discrepancies || []).map((d, i) => (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="p-2.5 font-mono">{d.dealer_code}</td>
                            <td className="p-2.5 font-medium">{d.dealer_name}</td>
                            <td className="p-2.5 text-right font-semibold">₹{Number(d.expected_inr || 0).toLocaleString('en-IN')}</td>
                            <td className="p-2.5 text-right text-slate-600">₹{Number(d.actual_inr || 0).toLocaleString('en-IN')}</td>
                            <td className="p-2.5 text-right text-rose-600 font-bold">₹{Number(d.variance_inr || 0).toLocaleString('en-IN')}</td>
                            <td className="p-2.5 text-slate-600">{d.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Card>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* PDF Preview Modal */}
      <Dialog open={!!previewPdf} onOpenChange={() => setPreviewPdf(null)}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b border-slate-200">
            <DialogTitle className="text-sm font-medium">Scheme Circular Document Preview</DialogTitle>
          </DialogHeader>
          <div className="flex-1 w-full bg-slate-100">
            {previewPdf && (
              <iframe
                src={previewPdf}
                title="Scheme PDF Preview"
                className="w-full h-full border-0"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Scheme Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Publish New Scheme Circular</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUploadSubmit} className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Scheme Title *</label>
              <Input
                placeholder="e.g. Galaxy S25 Series Upgrade Scheme"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Category</label>
              <Input
                placeholder="e.g. Smartphones / Tablets / Wearables"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Valid From *</label>
                <Input
                  type="date"
                  value={newValidFrom}
                  onChange={(e) => setNewValidFrom(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Valid Until *</label>
                <Input
                  type="date"
                  value={newValidUntil}
                  onChange={(e) => setNewValidUntil(e.target.value)}
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Description / Key Notes</label>
              <Input
                placeholder="Optional notes or eligibility criteria..."
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-slate-700">Scheme PDF Document *</label>
                {isCompressing && (
                  <span className="text-[10px] text-blue-600 font-medium animate-pulse">
                    {compressMsg || "Compressing PDF..."}
                  </span>
                )}
              </div>
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                disabled={isCompressing || uploadMutation.isPending}
                className="text-xs text-slate-500 block w-full file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                required
              />
              {compressedInfo && (
                <div className="mt-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-md flex items-center justify-between text-xs text-emerald-800">
                  <div className="flex items-center gap-1.5 font-medium">
                    <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                    <span>Original: {compressedInfo.originalSize} MB &rarr; Optimized: {compressedInfo.newSize} KB</span>
                  </div>
                  <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300 font-bold text-[10px]">
                    {compressedInfo.savedPct}% smaller
                  </Badge>
                </div>
              )}
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={isCompressing || uploadMutation.isPending} className="bg-[#0055FF] hover:bg-[#0044CC]">
                {isCompressing ? "Optimizing PDF..." : uploadMutation.isPending ? "Uploading..." : "Publish Circular"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
